/**
 * Décode le JWT ACODOC stocké en sessionStorage et retourne les infos
 * de l'utilisateur courant sans appel API.
 *
 * Le payload du token contient : sub, email, role, full_name, exp, iat.
 */
export interface CurrentUser {
  id: string
  email: string
  role: string
  full_name: string
  /** Initiales calculées depuis full_name, ou tag "[DEV]" en mode dev */
  initials: string
}

function decodePayload(token: string): Record<string, unknown> | null {
  try {
    const part = token.split('.')[1]
    const padded = part + '='.repeat((4 - (part.length % 4)) % 4)
    return JSON.parse(atob(padded.replace(/-/g, '+').replace(/_/g, '/')))
  } catch {
    return null
  }
}

function getInitials(fullName: string, email: string): string {
  const name = fullName?.trim()
  if (name) {
    return name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0])
      .join('')
      .toUpperCase()
  }
  // Fallback sur la partie locale de l'email (avant @)
  const local = email?.split('@')[0] ?? ''
  return (
    local
      .split(/[._-]/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0])
      .join('')
      .toUpperCase() || '?'
  )
}

export function useCurrentUser(): CurrentUser | null {
  const token = sessionStorage.getItem('acodoc_token')
  if (!token) return null

  const payload = decodePayload(token)
  if (!payload) return null

  const email = (payload.email as string) ?? ''
  const full_name = (payload.full_name as string) ?? ''
  const role = (payload.role as string) ?? ''

  // En mode dev (user fictif admin@acodoc.dev) on affiche "DEV"
  const isDev = email === 'admin@acodoc.dev'
  const initials = isDev ? 'DEV' : getInitials(full_name, email)

  return {
    id: (payload.sub as string) ?? '',
    email,
    role,
    full_name: isDev ? 'Admin Dev' : full_name,
    initials,
  }
}
